import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import { APP_NAME } from "../../lib/brand";
import { safeNext } from "../../lib/links";
import "./login.css";

/** What went wrong, in words someone can act on. */
function message(err: unknown): string {
  if (!(err instanceof ApiError)) return "Can’t reach the server. Check it’s running, then try again.";
  if (err.status === 401) return "That password didn’t work.";
  if (err.status === 429) return err.message; // "Too many attempts. Try again in N min."
  if (err.status === 403) return "This page’s address isn’t allowed to sign in. Open the app from its usual address.";
  return `Couldn’t sign in (${err.status}). Try again.`;
}

export function LoginPage() {
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(password);
      navigate(safeNext(params.get("next")), { replace: true });
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <div className="login-col">
        <div className="login-brand">
          <span className="login-mark" aria-hidden="true" />
          <span className="login-wordmark">{APP_NAME}</span>
        </div>

        <form className="login-card" onSubmit={submit} noValidate>
          <span className="login-bar" aria-hidden="true" />
          <h1 className="login-h">Sign in to present</h1>
          <p className="login-lead">Build talks from your notes, rehearse them, and run live polls with the room.</p>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <div className="login-input" data-error={error !== null}>
              <input
                id="password"
                type={shown ? "text" : "password"}
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                value={password}
                aria-invalid={error !== null}
                aria-describedby={error ? "login-error" : undefined}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
              />
              <button
                type="button"
                className="login-toggle"
                aria-pressed={shown}
                aria-controls="password"
                aria-label={shown ? "Hide password" : "Show password"}
                onClick={() => setShown((s) => !s)}
              >
                <EyeIcon open={!shown} />
                <span>{shown ? "Hide" : "Show"}</span>
              </button>
            </div>
          </div>

          {error && (
            <p className="login-error" id="login-error" role="alert">
              <AlertIcon />
              {error}
            </p>
          )}

          <button className="login-submit" type="submit" disabled={busy || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="login-foot">
          In the audience? <Link to="/j">Join with the code on screen</Link>
        </p>
      </div>
    </main>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8Z" />
      <circle cx="8" cy="8" r="2" />
      {!open && <path d="M2.5 13.5 13.5 2.5" />}
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 4.75v3.75M8 11.1v.15" />
    </svg>
  );
}
